import * as React from 'react'
import { Autocomplete, TextField, Typography, Box } from '@mui/material'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router-dom'

import { ParticipantProfileContext } from 'containers/ParticipantProfile'
import ParticipantCard from 'components/ParticipantProfile/ParticipantCard'
import {
  createTagsOptions,
  isTagAlreadySelected,
  validateParticiapantTags,
} from 'lib/participantProfile/Helper'
import { ParticipantTagInput } from 'types/Participants'
import { deleteParticipantTag } from 'api/Participant/ParticipantProfileTags/Action'
import { useAlert } from 'components/Shared/Alert'
import { setParticipantTags } from 'containers/ParticipantProfile/Redux/Actions'
import { useAppSelector, useAppDispatch } from 'redux/Hook'
import { fetchParticipantTags } from 'redux/thunks/ParticipantTags'

const ParticipantTags = () => {
  const {
    participantTags: { value: participantTagsValue = [], hasError },
  } = React.useContext(ParticipantProfileContext).state

  const { onAddingTag, dispatch } = React.useContext(
    ParticipantProfileContext,
  ).functions
  const [inputValue, setInputValue] = React.useState('')
  const { t } = useTranslation()
  const globalDispatch = useAppDispatch()
  const { id } = useParams() as { id: string }
  const { showAlert } = useAlert()
  const tags = useAppSelector((state) => state.participantTags.data)

  const handleDelete = async ({
    tagName,
    updatedTags,
  }: {
    tagName: string
    updatedTags: Array<ParticipantTagInput>
  }) => {
    const request = {
      userId: id,
      name: tagName,
    }

    const result = await deleteParticipantTag(request)

    if (result.status !== 204) {
      showAlert({ title: t('participant.tags.errorAlert') })

      return
    }

    dispatch(setParticipantTags(updatedTags))
  }

  const handleChange = async (
    _: React.SyntheticEvent,
    value: Array<ParticipantTagInput>,
    reason: string,
    details: any,
  ) => {
    if (reason === 'removeOption') {
      const deleteTagDetails = {
        tagName: details.option?.name,
        updatedTags: value,
      }

      handleDelete(deleteTagDetails)

      return
    }

    const selectedTag = details.option
    let requestTag = selectedTag.name

    if (isTagAlreadySelected(participantTagsValue, requestTag)) return null

    const hasNewTag = selectedTag.inputValue
    if (hasNewTag) {
      /**
       * Means it's a new tag.
       */
      requestTag = selectedTag.inputValue
    }

    const response = await onAddingTag(requestTag)

    if (response && hasNewTag) {
      /**
       * ⚠️ This will make sure that the
       * "newly" added tag is present in the options.
       */
      globalDispatch(fetchParticipantTags())
    }
  }

  const handleInputChange = (value: string) => {
    validateParticiapantTags(value, setInputValue)
  }

  return (
    <ParticipantCard>
      <Typography variant="h5" component="h5" mb={1} data-cy="tags-title">
        {t('participant.tags.title')}
      </Typography>
      <Typography
        variant="body1"
        color={'rgba(0, 0, 0, 0.54)'}
        mb={participantTagsValue.length ? 3 : 1}
      >
        {t('tags.description')}
      </Typography>
      <Box>
        <Autocomplete
          inputValue={inputValue}
          multiple
          limitTags={6}
          id="tags-standard"
          options={tags}
          value={participantTagsValue}
          getOptionLabel={(option) => {
            return option?.name || option?.inputValue || option
          }}
          defaultValue={participantTagsValue}
          popupIcon={null}
          freeSolo
          handleHomeEndKeys
          selectOnFocus
          onInputChange={(_, value) => handleInputChange(value)}
          clearOnBlur
          onChange={handleChange}
          isOptionEqualToValue={(option, value) => option.name === value.name}
          filterSelectedOptions
          filterOptions={(options, params) =>
            createTagsOptions(
              options,
              params,
              t('participant.tags.create'),
              participantTagsValue,
            )
          }
          renderInput={(params) => (
            <TextField
              {...params}
              variant="standard"
              label={t('participant.tags.inputLabel')}
              data-cy="tags-input"
              placeholder={t('participant.tags.placeholder')}
              error={hasError}
              helperText={t('participant.bulkTag.helpText')}
            />
          )}
          clearIcon={null}
        />
      </Box>
    </ParticipantCard>
  )
}

export default ParticipantTags
